import React, { memo, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { Tooltip, Button, Rate, Space, Table } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Alert from 'components/Alert';
import Spin from 'components/Spin';
import { formatDate } from 'helpers/date';
import { tap } from 'rxjs/operators';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IProductRating } from 'v2/interfaces/rating';
import productRatingService from 'v2/services/rating/product';

const List = memo(() => {
  useObservable(() => productRatingService.loadRatings(), []);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isLoading] = useObservable(() => productRatingService.isLoading(), []);

  const [data] = useObservable(() => productRatingService.getRatings(), []);
  const [pagination] = useObservable(() => productRatingService.getPaginationResponse(), []);

  const handleTablePaginationChange = (page: number, pageSize: number) => {
    if (pageSize !== pagination?.per_page) {
      productRatingService.setPagination({ ...pagination, per_page: pageSize, current_page: 1 });
      return;
    }

    productRatingService.setPagination({ ...pagination, current_page: page });
  };

  const handleClickModerate = useCallback(async (rating: IProductRating) => {
    setIsConfirmationOpen(true);
    const confirm = await Alert.show({
      message: `Deseja realmente tornar essa avaliação ${rating.moderated ? 'invisível' : 'visível'} na vitrine?`,
      title: `Tornar ${rating.moderated ? 'invisível' : 'visível'} na vitrine`,
      confirmation: true,
      buttonOk: 'Sim',
      buttonCancel: 'Não'
    });

    if (!confirm) {
      setIsConfirmationOpen(false);
      return;
    }

    productRatingService
      .updateModerate({ ...rating, ...{ moderated: !rating.moderated } })
      .pipe(
        tap(() => {
          toast.success('Avaliação atualizada com sucesso');
          productRatingService.updateListRatings();
        })
      )
      .subscribe();
    setIsConfirmationOpen(false);
    return;
  }, []);

  const handleClickVisivel = useCallback(async (rating: IProductRating) => {
    setIsConfirmationOpen(true);
    const confirm = await Alert.show({
      message: `Deseja realmente ${rating.visible ? 'não mostrar' : 'mostrar'} avaliação na página de venda?`,
      title: `${rating.visible ? 'Não mostrar' : 'Mostrar'} avaliação na página de venda`,
      confirmation: true,
      buttonOk: 'Sim',
      buttonCancel: 'Não'
    });

    if (!confirm) {
      setIsConfirmationOpen(false);
      return;
    }

    productRatingService
      .updateVisible({ ...rating, ...{ visible: !rating.visible } })
      .pipe(
        tap(() => {
          toast.success('Avaliação atualizada com sucesso');
          productRatingService.updateListRatings();
        })
      )
      .subscribe();
    setIsConfirmationOpen(false);
    return;
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Produto',
        key: 'product',
        render: (_: unknown, item: IProductRating) => `${item.product_id}-${item.product_title}`
      },
      {
        title: 'Cliente',
        dataIndex: 'rater',
        key: 'rater'
      },
      {
        title: 'Data',
        dataIndex: 'rated_at',
        key: 'rated_at',
        width: 140,
        render: (ratedAt: string) => formatDate(ratedAt)
      },
      {
        title: 'Avaliação',
        dataIndex: 'rate',
        key: 'rate',
        width: 180,
        render: (rate: number | string) => (
          <>
            <Rate value={Number(rate) / 2} disabled allowHalf className='text-sm' />
            <p className='pl-1.5'>{Number(rate) / 2} / 5</p>
          </>
        )
      },
      {
        title: 'Comentário',
        dataIndex: 'comment',
        key: 'comment',
        ellipsis: true,
        render: (comment: string) => (
          <Truncate
            className='w-full max-w-[75vw] overflow-hidden text-ellipsis whitespace-nowrap'
            count={120}
            final='...'
            title={comment}
          >
            {comment}
          </Truncate>
        )
      },
      {
        title: '',
        key: 'actions',
        width: 100,
        fixed: 'right' as const,
        align: 'right' as const,
        render: (_: unknown, item: IProductRating) => (
          <Space size={4}>
            <Tooltip placement='left' title={`Tornar ${item.moderated ? 'invisível' : 'visível'} na vitrine`}>
              <Button
                type='text'
                icon={item.moderated ? <Icon.Eye  /> : <Icon.EyeOff  />}
                shape='circle'
                onClick={() => handleClickModerate(item)}
                disabled={isConfirmationOpen}
              />
            </Tooltip>
            <Tooltip
              placement='top'
              title={`${item.visible ? 'Ocultar' : 'Mostrar'} avaliações na página de venda padrão da Eduzz`}
            >
              <Button
                type='text'
                icon={
                  <Icon.Users
                    className={item.visible ? 'text-(--uizz-antd-color-error)' : 'text-(--uizz-antd-color-primary)'}
                   />
                }
                shape='circle'
                onClick={() => handleClickVisivel(item)}
                disabled={isConfirmationOpen}
              />
            </Tooltip>
          </Space>
        )
      }
    ],
    [handleClickModerate, handleClickVisivel, isConfirmationOpen]
  );

  return (
    <div className='bg-surface-default mt-4 w-full rounded-sm shadow-sm'>
      <Table<IProductRating>
        rowKey={item => String(item.id)}
        columns={columns}
        dataSource={data || []}
        scroll={{ x: 800 }}
        loading={{ spinning: Boolean(isLoading), indicator: <Spin /> }}
        locale={{ emptyText: 'Nenhuma avaliação encontrada.' }}
        pagination={{
          current: pagination?.current_page || 1,
          pageSize: pagination?.per_page || 10,
          total: pagination?.total_items || 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 30, 40],
          onChange: handleTablePaginationChange,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`
        }}
      />
    </div>
  );
});

export default List;
